import React, { useEffect, useState } from 'react'
import {Alert,Box,Button,Card,CardContent,CardMedia,CircularProgress,FormControl,MenuItem,Select,Typography
} from '@mui/material'
import savedGameApi, { SavedGame } from '../../data/api/saved-game-api'
import rawgApi from '../../data/api/rawg/rawg-api'

import WindowsLogo from '../../assets/platforms/windows.svg'
import PlayStationLogo from '../../assets/platforms/playstation.svg'
import XboxLogo from '../../assets/platforms/xbox.svg'
import NintendoLogo from '../../assets/platforms/nintendo.svg'
import AppleLogo from '../../assets/platforms/apple.svg'
import AndroidLogo from '../../assets/platforms/android.svg'
import LinuxLogo from '../../assets/platforms/linux.svg'
import IOSLogo from '../../assets/platforms/ios.svg'

interface RawgPlatform {
  platform: {
    id: number
    name: string
    slug: string
  }
}


const GameLibraryPage = (): React.JSX.Element => {
  const [libraryGames, setLibraryGames] = useState<SavedGame[]>([])
  const [gamePlatforms, setGamePlatforms] = useState<
  Record<string, RawgPlatform[]>
>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('All')

  const loadLibrary = async () => {
    const userId = Number(localStorage.getItem('userId'))

    if (!userId) {
      setLibraryGames([])
      setGamePlatforms({})
      setError('Please log in to view your library.')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError('')

      const games = await savedGameApi.getLibrary(userId)

      setLibraryGames(games)

      const platformsData: Record<string, RawgPlatform[]> = {}

      await Promise.all(
        games.map(async (game) => {
          try {
            const response = await rawgApi.getGameDetails(
              Number(game.gameId)
            )

            const gameData =
              typeof response.data === 'string'
                ? JSON.parse(response.data)
                : response.data
                
            platformsData[game.gameId] =
              gameData.parent_platforms || []
          } catch {
            platformsData[game.gameId] = []
          }
        })
      )
      
      setGamePlatforms(platformsData)

      } catch (err) {
        console.error(err)
        setLibraryGames([])
        setGamePlatforms({})
        setError('Failed to load your game library.')
      } finally {
        setLoading(false)
      }
    }

  useEffect(() => {
    loadLibrary()
  }, [])

  const handleDelete = async (id?: number) => {
    if (!id) return

    try {
      await savedGameApi.deleteGame(id)
      setLibraryGames((games) => games.filter((game) => game.id !== id))
    } catch (err) {
      console.error(err)
      setError('Failed to remove game from library.')
    }
  }

  const handleFavoriteChange = async (game: SavedGame) => {
  if (!game.id) return

  try {
    const updatedGame = await savedGameApi.updateFavorite(
      game.id,
      !game.isFavorite
    )

    setLibraryGames((games) =>
      games.map((item) =>
        item.id === game.id ? updatedGame : item
      )
    )
  } catch (err) {
    console.error(err)
    setError('Failed to update favorite game.')
  }
}

const handleStatusChange = async (id: number, Status: string) => {
  try {
    const updatedGame = await savedGameApi.updateStatus(id, Status)

    setLibraryGames((games) =>
      games.map((game) =>
        game.id === id ? updatedGame : game
      )
    )
  } catch (err) {
    console.error(err)
    setError('Failed to update game status.')
  }
}




const platformIconStyle = {
  filter: 'brightness(0) invert(1)',
  opacity: 0.9
}

    const getPlatformIcon = (
      slug: string
    ): React.JSX.Element | null => {
      switch (slug) {
        case 'pc':
          return <img src={WindowsLogo} alt="Windows" width={20}style={platformIconStyle}  />

        case 'playstation':
          return (
            <img src={PlayStationLogo} alt="PlayStation" width={20} style={platformIconStyle}
            />
          )

        case 'xbox':
          return <img src={XboxLogo} alt="Xbox" width={20} style={platformIconStyle}/>

        case 'nintendo':
          return (
            <img
              src={NintendoLogo}
              alt="Nintendo"
              width={20}
              style={platformIconStyle}
            />
          )

        case 'mac':
          return <img src={AppleLogo} alt="Mac" width={20} style={platformIconStyle} />

        case 'android':
          return (
            <img src={AndroidLogo} alt="Android" width={20} style={platformIconStyle} />
          )

        case 'linux':
          return <img src={LinuxLogo} alt="Linux" width={20} style={platformIconStyle} />

        case 'ios':
          return <img src={IOSLogo} alt="iOS" width={20} style={platformIconStyle} />

        default:
          return null
      }
    }


  return (

    <Box
      sx={{
        background:
        'linear-gradient(180deg, #0b1220 0%, #0f1724 100%)',
        minHeight: '100vh',
        width: '100vw',
        marginLeft: 'calc(50% - 50vw)',
        padding: 4,
        color: '#f8fbff'
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Box sx={{ marginBottom: 4 }}>
          <Typography
            variant="h3"
            fontWeight="bold"
            gutterBottom
            sx={{ color: 'white' }}
          >
            My Game Library
          </Typography>

          <Typography
            variant="h6"
            sx={{ color: 'rgba(255,255,255,0.85)' }}
          >
            View the games saved to your NextQuest account.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, marginBottom: 4, flexWrap: 'wrap' }}>
          {['All', 'Playing', 'Completed', 'Backlog', 'Wishlist'].map((status) => (
            <Button
              key={status}
              variant={selectedStatus === status ? 'contained' : 'outlined'}
              onClick={() => setSelectedStatus(status)}
                sx={{
                    fontWeight: 'bold', color: 'white', backgroundColor: status === 'Playing' ? '#1976d2'
                     : status === 'Completed'  ? '#2e7d32'
                     : status === 'Backlog' ? '#ed6c02'
                     : status === 'Wishlist' ? '#9c27b0' : '#616161',

                  opacity: selectedStatus === status ? 1 : 0.6, 
                  '&:hover': { 
                    opacity: 1,
                    backgroundColor: status === 'Playing' ? '#1565c0'
                    : status === 'Completed'  ? '#1b5e20'
                     : status === 'Backlog' ? '#e65100'
                     : status === 'Wishlist' ? '#7b1fa2' : '#424242'
                  }
                }}


            >
              {status}
            </Button>
          ))}
          </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && libraryGames.length === 0 && (
          <Typography variant="h6">
            You have not saved any games yet.
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3
          }}
        >
          {libraryGames
          .filter(game => selectedStatus === 'All' || game.status === selectedStatus)
          .map((game) => (
            <Card
                key={game.id}
                sx={{
                  width: 320,
                  borderRadius: 3,
                  background:
                    'linear-gradient(180deg, rgba(36,58,88,0.96), rgba(24,40,64,0.96))',
                  border: '1px solid rgba(148,163,184,0.35)',
                  color: '#f8fbff',
                  boxShadow: 4,
                  transition: '0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 8
                  }
                }}
              >
              {game.imageUrl && (
                <CardMedia
                  component="img"
                  height="180"
                  image={game.imageUrl}
                  alt={game.title}
                />
              )}

              <CardContent>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ color: '#f8fbff' }}
                >
                  {game.title}
                </Typography>

                <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1.5,
                  minHeight: 20
                }}
              >
                {gamePlatforms[game.gameId]?.map((platform) => (
                  <Box
                    key={platform.platform.id}
                    title={platform.platform.name}
                    sx={{
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {getPlatformIcon(platform.platform.slug)}
                  </Box>
                ))}
              </Box>



                <FormControl fullWidth sx={{ mb: 2 }}>
                  <Select
                  value={game.status}
                   onChange={(event) =>
                    handleStatusChange(game.id!, event.target.value as string)
                   }
                    size="small"
                    sx={{
                      fontWeight: 'bold',
                      color: 'white',
                      backgroundColor: game.status === 'Playing' ? '#1976d2'
                      : game.status === 'Completed' ? '#2e7d32'
                      : game.status === 'Backlog' ? '#ed6c02' : '#9c27b0',
                      '& .MuiSelect-icon': {
                        color: 'white'
                      }
                    }}

                    
                    >
                      <MenuItem value="Backlog">Backlog</MenuItem>
                      <MenuItem value="Playing">Playing</MenuItem>
                      <MenuItem value="Completed">Completed</MenuItem>
                      <MenuItem value="Wishlist">Wishlist</MenuItem>
                  </Select>
                </FormControl>

              
              <Typography
                variant="body2"
                sx={{ color: '#c2cedd' }}
                gutterBottom
              >
                Released:{' '}
                {game.releaseDate
                  ? new Date(game.releaseDate).toLocaleDateString('en-US', {
                      month: 'numeric',
                      day: 'numeric',
                      year: 'numeric'
                    })
                  : 'Unknown'}
              </Typography>

              <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleFavoriteChange(game)}
                  sx={{
                    mt: 2,
                    backgroundColor: game.isFavorite ? '#1d4ed8' : '#3b82f6',
                    color: '#fff',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: game.isFavorite ? '#1e40af' : '#2563eb'
                    }
                  }}
                >
                  {game.isFavorite
                    ? 'Remove from Favorites'
                    : 'Add to Favorites'}
                </Button>

              <Button
                variant="contained"
                fullWidth
                onClick={() => handleDelete(game.id)}
                sx={{
                  mt: 1,
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#2563eb'
                  }
                }}
              >
                Remove from Library
              </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default GameLibraryPage
